import { useState, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import searchConsoleService, {
  GoogleConnectionStatus,
  SearchConsoleProperty,
  SearchConsoleSummary,
} from '@/services/searchConsole';
import CEOxSEOComponent, { DatePreset } from './CEOxSEO.component';

const DEFAULT_SUMMARY: SearchConsoleSummary = {
  current: { totalClicks: 0, totalImpressions: 0, avgCtr: 0, avgPosition: 0 },
  previous: { totalClicks: 0, totalImpressions: 0, avgCtr: 0, avgPosition: 0 },
  topQueries: [],
  topPages: [],
  dailyTrend: [],
};

const PRESET_DAYS: Record<DatePreset, number> = {
  '7d': 7,
  '28d': 28,
  '3m': 90,
  '6m': 180,
  '12m': 365,
};

const CEOxSEOContainer = function (): JSX.Element {
  const orgId = getActiveOrgId();
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('28d');

  const days = PRESET_DAYS[datePreset];

  const { data: connectionStatus } = useSeoFetch<GoogleConnectionStatus>(
    ['ceox-gsc-status', orgId],
    () => searchConsoleService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = connectionStatus?.isConnected ?? false;
  const defaultSite = connectionStatus?.siteUrl ?? '';

  const { data: availableSites = [] } = useSeoFetch<SearchConsoleProperty[]>(
    ['ceox-gsc-sites', orgId],
    () => searchConsoleService.getAvailableSites(),
    { retry: false, enabled: isConnected }
  );

  const activeSite = selectedSite ?? defaultSite;

  const { data: summary, isLoading } = useSeoFetch<SearchConsoleSummary>(
    ['ceox-gsc-summary', orgId, activeSite, days],
    () => searchConsoleService.getSummary(activeSite, days),
    { retry: false, enabled: isConnected && !!activeSite }
  );

  const handleSiteChange = useCallback((siteUrl: string) => {
    setSelectedSite(siteUrl);
  }, []);

  const handleDatePresetChange = useCallback((preset: DatePreset) => {
    setDatePreset(preset);
  }, []);

  return (
    <CEOxSEOComponent
      isConnected={isConnected}
      siteUrl={activeSite}
      availableSites={availableSites}
      onSiteChange={handleSiteChange}
      summary={summary ?? DEFAULT_SUMMARY}
      isLoading={isLoading}
      datePreset={datePreset}
      onDatePresetChange={handleDatePresetChange}
    />
  );
};

export default CEOxSEOContainer;
